import { Destroyable } from "./destroyable.js";
import { wrongBinding } from "./errors";
import { IValue } from "./ivalue.js";
import { Expression } from "../value/expression";
import { Reference } from "../value/reference";
import { Pointer } from "../value/pointer";
import { Mirror } from "../value/mirror";
/**
 * Private stuff of a reactive object
 * @class ReactivePrivate
 * @extends Destroyable
 */
export class ReactivePrivate extends Destroyable {
    constructor() {
        super();
        /**
         * A list of user-defined values
         * @type {Set}
         */
        this.watch = new Set;
        /**
         * A list of user-defined bindings
         * @type {Set}
         */
        this.bindings = new Set;
        /**
         * A list of user defined models
         */
        this.models = new Set;
        /**
         * Reactivity switch state
         * @type {boolean}
         */
        this.enabled = true;
        /**
         * The frozen state of object
         * @type {boolean}
         */
        this.frozen = false;
        this.$seal();
    }
    $destroy() {
        var _a;
        this.watch.forEach(value => value.$destroy());
        this.watch.clear();
        this.bindings.forEach(binding => binding.$destroy());
        this.bindings.clear();
        (_a = this.freezeExpr) === null || _a === void 0 ? void 0 : _a.$destroy();
        super.$destroy();
    }
}
/**
 * A reactive object
 * @class Reactive
 * @extends Destroyable
 */
export class Reactive extends Destroyable {
    constructor($) {
        super();
        this.$ = $ || new ReactivePrivate;
    }
    /**
     * Create a reference
     * @param value {*} value to reference
     */
    $ref(value) {
        const $ = this.$;
        const ref = new Reference(value);
        $.watch.add(ref);
        return ref;
    }
    /**
     * Create a mirror
     * @param value {IValue} value to mirror
     */
    $mirror(value) {
        const mirror = new Mirror(value, false);
        this.$.watch.add(mirror);
        return mirror;
    }
    /**
     * Create a forward-only mirror
     * @param value {IValue} value to mirror
     */
    $forward(value) {
        const mirror = new Mirror(value, true);
        this.$.watch.add(mirror);
        return mirror;
    }
    /**
     * Creates a pointer
     * @param value {*} default value to point
     * @param forwardOnly {boolean} forward only sync
     */
    $point(value, forwardOnly = false) {
        const $ = this.$;
        const ref = value instanceof IValue ? value : new Reference(value);
        const pointer = new Pointer(ref, forwardOnly);
        // when value is an ivalue will be equal to ref
        if (value !== ref) {
            $.watch.add(ref);
        }
        $.watch.add(pointer);
        return pointer;
    }
    /**
     * Register a model
     * @param model
     */
    $register(model) {
        this.$.models.add(model);
        return model;
    }
    $watch(func, v1, v2, v3, v4, v5, v6, v7, v8, v9) {
        const $ = this.$;
        $.watch.add(new Expression(func, !this.$.frozen, v1, v2, v3, v4, v5, v6, v7, v8, v9));
    }
    $bind(func, v1, v2, v3, v4, v5, v6, v7, v8, v9) {
        const res = new Expression(func, !this.$.frozen, v1, v2, v3, v4, v5, v6, v7, v8, v9);
        const $ = this.$;
        $.watch.add(res);
        return res;
    }
    /**
     * Enable reactivity of fields
     */
    $enable() {
        const $ = this.$;
        if (!$.enabled) {
            $.watch.forEach(watcher => {
                watcher.enable();
            });
            $.models.forEach(model => {
                model.enableReactivity();
            });
            $.enabled = true;
        }
    }
    /**
     * Disable reactivity of fields
     */
    $disable() {
        const $ = this.$;
        if ($.enabled) {
            $.watch.forEach(watcher => {
                watcher.disable();
            });
            $.models.forEach(model => {
                model.disableReactivity();
            });
            $.enabled = false;
        }
    }
    /**
     * Disable/Enable reactivity of object fields with feedback
     * @param cond {IValue} show condition
     * @param onOff {function} on show feedback
     * @param onOn {function} on hide feedback
     */
    $bindAlive(cond, onOff, onOn) {
        const $ = this.$;
        if ($.freezeExpr) {
            throw wrongBinding("this component already have a freeze state");
        }
        if ($.watch.has(cond)) {
            throw wrongBinding("freeze state must be bound to an external component");
        }
        $.freezeExpr = new Expression((cond) => {
            $.frozen = !cond;
            if (cond) {
                onOn === null || onOn === void 0 ? void 0 : onOn();
                this.$enable();
            }
            else {
                onOff === null || onOff === void 0 ? void 0 : onOff();
                this.$disable();
            }
        }, true, cond);
        return this;
    }
    $destroy() {
        super.$destroy();
        this.$.$destroy();
        this.$ = null;
    }
}
